import type { ScoreConfig } from "@solitaire/game-engine";

//  Spieltisch-Hintergrundfarben --------------------------------------------

export const TABLE_BACKGROUNDS = {
  casinoGreen:    { label: "Casino Green",    color: "#4E6A54" },
  filzGreen:      { label: "Dark Green",      color: "#0f4219" },
  waldGreen:      { label: "Waldgrün",        color: "#008000" },
  pokerFelt:      { label: "Poker-Felt",      color: "#800080" },
  electricPurple: { label: "Electric Purple", color: "#A020F0" },
  rebeccaPurple:  { label: "RebeccaPurple",   color: "#663399" },
} as const;

export type TableBackgroundKey = keyof typeof TABLE_BACKGROUNDS;

//  Spielkonfiguration ----------------------------------------------------

/**
 * Zentrale Spielkonfiguration.
 * Alle Werte hier können frei angepasst werden.
 */
export const GAME_CONFIG = {
  //  Loading Screen -------------------------------------------------------
  /** Hintergrundfarbe des Loading Screens. */
  loadingScreenColor: "#164823",

  //  Hintergrund ----------------------------------------------------------
  /** Aktive Spieltisch-Farbe (Schlüssel aus TABLE_BACKGROUNDS). */
  tableBackground: "filzGreen" as TableBackgroundKey,
  //  Tableau ----------------------------------------------------------
  /** Sichtbarer Bereich einer verdeckten Tableau-Karte in px (kleiner = enger). */
  tableauFaceDownPeek: 14,
  /** Sichtbarer Bereich einer aufgedeckten Tableau-Karte in px (größer = luftiger). */
  tableauFaceUpPeek: 38,

  //  Auto-Ass ----------------------------------------------------------
  /**
   * Verzögerung bevor ein aufgedecktes Ass automatisch auf die Foundation
   * fliegt (in ms). Kurz genug damit es "sofort" wirkt, aber lang genug
   * damit der Spieler die Karte kurz sieht.
   */
  autoAceDelayMs: 280,

  //  Auto-Complete --------------------------------------------------------
  /**
   * Verzögerung zwischen den automatischen Foundation-Zügen am Ende in ms.
   * Kleiner = schneller (z. B. 30), größer = gemächlicher (z. B. 150).
   */
  autoCompleteDelayMs: 100,

  //  Undo --------------------------------------------------------------
  /**
   * Maximale Anzahl der erlaubten Undo-Züge pro Spiel.
   * 0 = unbegrenzt.
   */
  maxUndoCount: 3,

  //  Kartenflug-Animation -----------------------------------------------
  /** Standard-Speed: Auto-Move, Drag-Flyback, Tableau?Foundation etc. */
  flySpeed: {
    slow:   { stiffness: 50,  damping: 14 },
    normal: { stiffness: 100, damping: 18 },
    fast:   { stiffness: 220, damping: 26 },
  },
  /** Stock→Waste: schnell, kritisch gedämpft (damping ≈ 2√stiffness → kein Bounce). */
  stockFlySpeed: {
    slow:   { stiffness: 4000, damping: 128 },
    normal: { stiffness: 6000, damping: 156 },
    fast:   { stiffness: 10000, damping: 200 },
  },
  /** Auto-Complete / Victory: schnell, feierlich zackig. */
  victoryFlySpeed: {
    slow:   { stiffness: 200, damping: 26 },
    normal: { stiffness: 400, damping: 32 },
    fast:   { stiffness: 700, damping: 40 },
  },

  //  Punkte ------------------------------------------------------------
  scoring: {
    /**
     * Zeitlimit in Sekunden. Wer das Set darunter löst, bekommt den vollen
     * Zeitbonus. Verbleibende Sekunden × pointsPerSecond. (3 min = 180)
     */
    thresholdSeconds: 300,
    /** Zeitbonus pro verbleibender Sekunde bei Sieg. */
    pointsPerSecond: 15,
    /**
     * Divisor für den Zeitbonus wenn das Set abgebrochen (nicht gewonnen) wird.
     * Zeitbonus-Abbruch = Zeitbonus-Sieg ÷ notCompletedDivisor.
     */
    notCompletedDivisor: 20,
    /** Bonus pro Karte, die auf die Foundation gelegt wird (max. 52 × 5 = 260). */
    pointsPerFoundationCard: 5,
    /** Einmaliger Bonus für alle 52 Karten abgelegt (Gewinn). */
    winBonus: 100,
    /** Bonus pro aufgedeckter verdeckter Tableau-Karte (max. 21 × 5 = 105). */
    pointsPerFlip: 5,
    /**
     * Erste N Rotationen (Waste ? Stock) sind kostenfrei.
     * Ab Rotation N+1 gibt es rotationPenalty Abzug pro Extra-Rotation.
     */
    rotationThreshold: 2,
    /** Abzug pro Extra-Rotation (ab der 3. Rotation). */
    rotationPenalty: 20,
    /** Abzug pro Shake (Doppelklick ohne gültiges Ziel). */
    shakePenalty: 20,
  } satisfies ScoreConfig,
} as const;
